// 📁 Imports
import validator from "validator";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

import {
  showErrorMessage,
  showSuccessMessage,
  clearErrorMessage,
} from "@/utils/input-message.js";

// 🌐 Error message translations mapping
const ERROR_TRANSLATIONS = {
  'required': 'VALIDACE_POVINNE_POLE',
  'email': 'VALIDACE_SPATNY_EMAIL', 
  'phone': 'VALIDACE_SPATNY_TELEFON',
  'phone-format': 'VALIDACE_SPATNY_TELEFON_S_PREDVOLBOU',
  'min-length': 'VALIDACE_MIN_POCET_ZNAKU',
  'max-length': 'VALIDACE_MAX_POCET_ZNAKU',
  'min-number': 'VALIDACE_MIN_HODNOTA',
  'max-number': 'VALIDACE_MAX_HODNOTA',
  'min-file': 'VALIDACE_MIN_POCET_SOUBORU',
  'max-file': 'VALIDACE_MAX_POCET_SOUBORU',
  'checked': 'VALIDACE_POVINNE_POLE',
  'radio-checked': 'VALIDACE_POVINNE_POLE'
};

// 📐 Helper function to get translated error message
const getErrorTranslation = (errorType, customMessage = null) => {
  if (customMessage) return customMessage;
  return ERROR_TRANSLATIONS[errorType] || `VALIDACE_${errorType.toUpperCase().replace('-', '_')}`;
};

// import { showResponseMessage } from "./response-message.js";

// import { buttonLoaderShow, buttonLoaderHide } from "../components/button.js";

// 💾 Variables
// const formValidate = document.querySelector(".js-form-validate");
const validateInputs = document.querySelectorAll("[data-validate]");
// let errors = [];

// 📐 Functions

// Rules
// ❗ Validate is not empty
const isRequired = (value, input, showValidation = true) => {
  let isInvalid = validator.isEmpty(value || "", {
    ignore_whitespace: true,
  });
  const isValid = !isInvalid;

  if (value === "[]") {
    isInvalid = true;
  }

  if (isInvalid) {
    if (showValidation) {
      input.setCustomValidity(" ");
      showErrorMessage(input, "[data-validate-required-message]");
    }
  } else {
    input.setCustomValidity("");
    if (showValidation) {
      showSuccessMessage(input);
    }
  }

  return isValid;
};

// ❗ Validate phone
const isPhoneWithCountryCode = (value, input, showValidation = true) => {
  const inputWrapper = input.closest("[data-input-phone-country-wrapper]");
  const inputHidden = inputWrapper.querySelector("[data-input-phone-value]");
  let isValid = false;

  // If value is empty, it's invalid for phone format validation
  if (value.length === 0) {
    isValid = false;
  } else {
    if (input.dataset.inputValidatePhoneCountry !== "INTERNATIONAL") {
      try {
        inputHidden.value = parsePhoneNumber(
          value,
          input.dataset.inputValidatePhoneCountry,
        ).number;

        isValid = parsePhoneNumber(
          value,
          input.dataset.inputValidatePhoneCountry,
        ).isPossible();
      } catch (err) {
        inputHidden.value = "";
        isValid = false;
      }
    } else {
      try {
        isValid = parsePhoneNumber(value, null).isPossible();
        inputHidden.value = parsePhoneNumber(value, null).number;
      } catch (err) {
        inputHidden.value = "";
        isValid = false;
      }
    }
  }

  if (!isValid && showValidation) {
    input.setCustomValidity(" ");
    showErrorMessage(inputHidden, "[data-validate-phone-format-message]");
  } else if (isValid && showValidation) {
    showSuccessMessage(input);
  }
  
  return isValid;
};

// ❗ Validate phone
const isPhone = (value, input, showValidation = true) => {
  // If value is empty, it's invalid for phone validation
  let isValid = false;
  
  if (value.length > 0) {
    isValid = isValidPhoneNumber(value);
  }

  if (!isValid && showValidation) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-phone-message]");
  } else if (isValid && showValidation) {
    showSuccessMessage(input);
  }
  
  return isValid;
};

// ❗ Validate radio is checked
const isCheckedGroup = (input) => {
  const radio = document.querySelector(`input[name="${input.name}"]:checked`);
  const radioRequired = document.querySelector(
    `input[name="${input.name}"][data-validate-radio-checked][data-validate-radio-checked]`,
  );
  const isValid = radio !== null && radioRequired !== null ? true : false;
  const isInvalid = !isValid;

  if (isInvalid) {
    radioRequired.setCustomValidity(" ");
    showErrorMessage(radioRequired, "[data-validate-radio-checked-message]");
  }
  return isValid;
};

// Validate is checked
const isChecked = (input, showValidation = true) => {
  const isInvalid = !input.checked;
  const isValid = input.checked;
  if (isInvalid) {
    if (showValidation) {
      input.setCustomValidity(" ");
      showErrorMessage(input, "[data-validate-checked-message]");
    }
  } else {
    input.setCustomValidity("");
    if (showValidation) {
      // For checkbox, we don't show success message, just hide error
      const errorMessage = input.closest('[data-validate-input-wrapper]')?.querySelector('[data-validate-checked-message]');
      if (errorMessage) {
        errorMessage.style.display = 'none';
      }
    }
  }

  return isValid;
};

// Validate is email
const isEmail = (value, input) => {
  const isInvalid = !validator.isEmail(value);
  const isValid = !isInvalid;

  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-email-message]");
  } else {
    showSuccessMessage(input);
  }
  return isValid;
};

// ❗ Validate is min length
const isMinLength = (value, input) => {
  const inputData = input.dataset;
  const isInvalid = !validator.isLength(value.trim(), {
    min: parseInt(inputData.validateMinLength),
    max: undefined,
  });
  const isValid = !isInvalid;

  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-min-length-message]");
  } else {
    showSuccessMessage(input);
  }

  return isValid;
};

// ❗ Validate is max length
const isMaxLength = (value, input) => {
  const inputData = input.dataset;
  const isInvalid = !validator.isLength(value.trim(), {
    min: undefined,
    max: parseInt(inputData.validateMaxLength),
  });
  const isValid = !isInvalid;

  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-max-length-message]");
  } else {
    showSuccessMessage(input);
  }

  return isValid;
};

// ❗ Validate is min number
const isMinNumber = (value, input) => {
  const inputData = input.dataset;
  const numValue = parseFloat(value);
  const minNumber = parseFloat(inputData.validateMinNumber);

  const isInvalid = isNaN(numValue) || numValue < minNumber;
  const isValid = !isInvalid;

  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-min-number-message]");
  } else {
    showSuccessMessage(input);
  }

  return isValid;
};

// ❗ Validate is max number
const isMaxNumber = (value, input) => {
  const inputData = input.dataset;
  const numValue = parseFloat(value);
  const maxNumber = parseFloat(inputData.validateMaxNumber);

  const isInvalid = isNaN(numValue) || numValue > maxNumber;
  const isValid = !isInvalid;

  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-max-number-message]");
  } else {
    showSuccessMessage(input);
  }

  return isValid;
};

// ❗ Validate is min file
const isMinFile = (value, input) => {
  const inputData = input.dataset;
  const isInvalid = input.files.length < Number(inputData.validateMinFile);
  const isValid = !isInvalid;
  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-min-file-message]");
  } else {
    showSuccessMessage(input);
  }
  return isValid;
};

// ❗ Validate is max file
const isMaxFile = (value, input) => {
  const inputData = input.dataset;
  const isInvalid = input.files.length > Number(inputData.validateMaxFile);
  const isValid = !isInvalid;
  if (isInvalid) {
    input.setCustomValidity(" ");
    showErrorMessage(input, "[data-validate-max-file-message]");
  } else {
    showSuccessMessage(input);
  }
  return isValid;
};

let errors = [];

// Add error
const addError = (isInavalid, input) => {
  if (isInavalid && !errors.includes(input)) {
    errors.push(input);
  }
};

const removeError = (input) => {
  errors = errors.filter((el) => el !== input);
};

// 📐 Validator for inputs (run all rules)
const inputValidator = (input, inputTypeValid, showValidation = true) => {
  const value = input.value;
  let inputErrors = []; // Local errors array for this input

  if (showValidation) {
    input.setCustomValidity("");
    clearErrorMessage(input);
  }
  
  // Remove this input from global errors first
  removeError(input);

  // Checked (radio group)
  if (inputTypeValid.validateRadioChecked !== undefined) {
    const isValid = isCheckedGroup(input);

    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  }

  // Checked
  if (inputTypeValid.validateChecked !== undefined) {
    const isValid = isChecked(input, showValidation);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  }

  // Email format
  if (inputTypeValid.validateEmail !== undefined && value.length !== 0) {
    const isValid = isEmail(value, input);
    if (!isValid) {
      addError(true, input);
      inputErrors.push({
        type: "email",
        message: getErrorTranslation("email")
      });
    }
  }

  // Min lenght chart
  if (inputTypeValid.validateMinLength !== undefined && value.length !== 0) {
    const isValid = isMinLength(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Max lenght chart
  if (inputTypeValid.validateMaxLength !== undefined && value.length !== 0) {
    const isValid = isMaxLength(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Min number
  if (inputTypeValid.validateMinNumber !== undefined && value.length !== 0) {
    const isValid = isMinNumber(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Max number
  if (inputTypeValid.validateMaxNumber !== undefined && value.length !== 0) {
    const isValid = isMaxNumber(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Min file
  if (inputTypeValid.validateMinFile !== undefined && value.length !== 0) {
    const isValid = isMinFile(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Max file
  if (inputTypeValid.validateMaxFile !== undefined && value.length !== 0) {
    const isValid = isMaxFile(value, input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  } else {
    removeError(input);
  }

  // Phone (select country code)
  if (inputTypeValid.validatePhoneFormat !== undefined) {
    // Always validate phone format - empty values will be invalid
    const isValid = isPhoneWithCountryCode(value, input, showValidation);
    if (!isValid) {
      addError(true, input);
      inputErrors.push({
        type: "phone-format",
        message: getErrorTranslation("phone-format")
      });
    }
  }

  // Phone
  if (inputTypeValid.validatePhone !== undefined) {
    const isValid = isPhone(value, input, showValidation);
    if (!isValid) {
      addError(true, input);
      inputErrors.push({
        type: "phone",
        message: getErrorTranslation("phone")
      });
    }
  }

  // Required
  if (inputTypeValid.validateRequired !== undefined) {
    const isValid = isRequired(value, input, showValidation);
    if (!isValid) {
      addError(true, input);
      inputErrors.push({
        type: "required",
        message: getErrorTranslation("required")
      });
    }
  }

  // Checked (radio group)
  if (inputTypeValid.validateRadioChecked !== undefined) {
    const isValid = isCheckedGroup(input);

    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  }

  // Checked
  if (inputTypeValid.validateChecked !== undefined) {
    const isValid = isChecked(input);
    if (isValid) {
      removeError(input);
    } else {
      addError(!isValid, input);
    }
  }

  return { errors: inputErrors };
};

// 🖱️ Event listener
// Single input validation
if (validateInputs.length !== 0) {
  validateInputs.forEach((input) => {
    const inputTypeValid = input.dataset;

    input.addEventListener("input", (e) => {
      const input = e.target;
      inputValidator(input, inputTypeValid);
    });
  });
}

// 📤 Export
export { isPhoneWithCountryCode, inputValidator, ERROR_TRANSLATIONS, getErrorTranslation };
